import { useMemo } from 'react';
import { useDorisApplyEntityFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';
import { useDorisData, UseDorisDataProps } from 'src/utils/Hooks/useDorisData';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import { useComparisonWeekIds } from 'src/components/BeaconV2Components/SplineChartV2/useComparisonWeekIds';
import { getWeekIdRange } from 'src/utils/dateUtils';
import { TrafficSummaryResponse } from './types';

/**
 * Arguments for the data fetcher hook
 */
export interface UseDorisMetricDataFetcherArgs {
  queryKey: string;
  metricType: string;
  methodName: string;
  resultCount: number;
  showMarketShare: boolean;
  skipComparison: boolean;
  useLatestWeek: boolean;
  baseParamsBuilder?: (params: Record<string, unknown>) => Record<string, unknown>;
  getComparisonParamsFromPrimary?: (primaryData: TrafficSummaryResponse) => {
    categoryIds?: number[];
    subcategoryIds?: number[];
    brandIds?: number[];
    segmentIds?: string[];
  };
  marketShareNumeratorRequestBuilder?: (params: any) => any;
}

/**
 * Return type for the data fetcher hook
 */
export interface UseDorisMetricDataFetcherResult {
  primaryData: TrafficSummaryResponse | undefined;
  comparisonData: TrafficSummaryResponse | undefined;
  isPrimaryLoading: boolean;
  isComparisonLoading: boolean;
  // Time period info
  startWeekId: number;
  endWeekId: number;
  comparisonStartWeekId: number;
  comparisonEndWeekId: number;
  weekIdsInRange: number[];
  comparisonWeekIdsInRange: number[];
  comparisonWeekIdsForMainPeriod: number[];
  isLastWeekWithPriorYear: boolean;
  isYTDWithPriorYear: boolean;
  // Base params for market share hook
  baseParams: Record<string, unknown>;
}

/**
 * Hook that handles fetching primary and comparison metric data from Doris
 * 
 * This extracts the data fetching logic from useDorisMetricByWeekId
 */
export const useDorisMetricDataFetcher = ({
  queryKey,
  metricType,
  methodName,
  resultCount,
  showMarketShare,
  skipComparison,
  useLatestWeek,
  baseParamsBuilder,
  getComparisonParamsFromPrimary,
  marketShareNumeratorRequestBuilder
}: UseDorisMetricDataFetcherArgs): UseDorisMetricDataFetcherResult => {
  const applyEntityFilters = useDorisApplyEntityFilters();
  const retailerId = useRetailerId();

  // Use the comparison week IDs hook which properly handles 52/53 week year differences via the API
  const {
    startWeekId: defaultStartWeekId,
    endWeekId,
    comparisonStartWeekId: defaultComparisonStartWeekId,
    comparisonEndWeekId: defaultComparisonEndWeekId,
    isLastWeekWithPriorYear,
    isYTDWithPriorYear,
    comparisonWeekIdsForMainPeriod
  } = useComparisonWeekIds();

  // Support useLatestWeek override - when true, only fetch the latest week
  const startWeekId = useMemo(() => {
    if (useLatestWeek) {
      return endWeekId;
    }
    return defaultStartWeekId;
  }, [useLatestWeek, endWeekId, defaultStartWeekId]);

  // For useLatestWeek, comparison should also just be the latest week's comparison
  const comparisonStartWeekId = useMemo(() => {
    if (useLatestWeek && comparisonWeekIdsForMainPeriod.length > 0) {
      return comparisonWeekIdsForMainPeriod[comparisonWeekIdsForMainPeriod.length - 1];
    }
    return defaultComparisonStartWeekId;
  }, [useLatestWeek, comparisonWeekIdsForMainPeriod, defaultComparisonStartWeekId]);

  const comparisonEndWeekId = useMemo(() => {
    if (useLatestWeek && comparisonWeekIdsForMainPeriod.length > 0) {
      return comparisonWeekIdsForMainPeriod[comparisonWeekIdsForMainPeriod.length - 1];
    }
    return defaultComparisonEndWeekId;
  }, [useLatestWeek, comparisonWeekIdsForMainPeriod, defaultComparisonEndWeekId]);

  // Generate week ID ranges using the proper utility that handles week 53
  const weekIdsInRange = useMemo(
    () => getWeekIdRange(startWeekId, endWeekId),
    [startWeekId, endWeekId]
  );

  const comparisonWeekIdsInRange = useMemo(() => {
    if (skipComparison) {
      return [];
    }
    return getWeekIdRange(comparisonStartWeekId, comparisonEndWeekId);
  }, [comparisonStartWeekId, comparisonEndWeekId, skipComparison]);

  // Apply entity filters to the base parameters
  const baseParams = useMemo(() => {
    const initialParams = {
      metricType,
      retailerId: +retailerId
    };

    const paramsToUse = baseParamsBuilder ? baseParamsBuilder(initialParams) : initialParams;
    return applyEntityFilters(paramsToUse, showMarketShare);
  }, [metricType, retailerId, applyEntityFilters, showMarketShare, baseParamsBuilder]);

  // Fetch primary data
  const { data: primaryData, isLoading: isPrimaryLoading } = useDorisData<TrafficSummaryResponse>({
    methodName,
    metricType,
    queryKey: `${queryKey}_${metricType}_${startWeekId}_${endWeekId}`,
    resultCount,
    ...baseParams,
    customTimePeriods: {
      startWeekId,
      endWeekId
    },
    additionalParams: showMarketShare && marketShareNumeratorRequestBuilder 
      ? marketShareNumeratorRequestBuilder(baseParams) 
      : {},
    skipComparison: true,
    enabled: startWeekId > 0 && endWeekId > 0,
    queryOptions: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  });

  // Prepare parameters for comparison query based on primary data if needed
  const comparisonParams = useMemo(() => {
    if (skipComparison) {
      return null;
    }

    // If we need to transform the primary data to get comparison params
    if (getComparisonParamsFromPrimary && primaryData) {
      const dynamicParams = getComparisonParamsFromPrimary(primaryData);
      const comparisonBaseParams = {
        ...baseParams,
        startWeekId: comparisonStartWeekId,
        endWeekId: comparisonEndWeekId,
        categoryIds: dynamicParams.categoryIds,
        subcategoryIds: dynamicParams.subcategoryIds,
        brandIds: dynamicParams.brandIds,
        segmentIds: dynamicParams.segmentIds
      };
      return applyEntityFilters(comparisonBaseParams);
    }

    // Basic comparison params without transformation
    const comparisonBaseParams = {
      ...baseParams,
      startWeekId: comparisonStartWeekId,
      endWeekId: comparisonEndWeekId
    };
    return applyEntityFilters(comparisonBaseParams, showMarketShare);
  }, [
    skipComparison,
    getComparisonParamsFromPrimary,
    primaryData,
    baseParams,
    comparisonStartWeekId,
    comparisonEndWeekId,
    applyEntityFilters,
    showMarketShare
  ]);

  // Fetch comparison data
  const { data: comparisonData, isLoading: isComparisonLoading } = useDorisData<TrafficSummaryResponse>({
    methodName,
    metricType,
    queryKey: `comparison_${queryKey}_${metricType}_${comparisonStartWeekId}_${comparisonEndWeekId}`,
    resultCount,
    categoryIds: comparisonParams?.categoryIds,
    subcategoryIds: comparisonParams?.subcategoryIds,
    brandIds: comparisonParams?.brandIds,
    segmentIds: comparisonParams?.segmentIds,
    minRetailPrice: comparisonParams?.minRetailPrice,
    trendIds: comparisonParams?.trendIds,
    maxRetailPrice: comparisonParams?.maxRetailPrice,
    customTimePeriods: {
      startWeekId: comparisonStartWeekId,
      endWeekId: comparisonEndWeekId
    },
    additionalParams: showMarketShare && marketShareNumeratorRequestBuilder 
      ? marketShareNumeratorRequestBuilder(comparisonParams) 
      : {},
    skipComparison: true,
    enabled: !skipComparison && comparisonStartWeekId > 0 && comparisonEndWeekId > 0 && (getComparisonParamsFromPrimary ? !!primaryData : true),
    queryOptions: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  });

  return {
    primaryData,
    comparisonData,
    isPrimaryLoading,
    isComparisonLoading,
    startWeekId,
    endWeekId,
    comparisonStartWeekId,
    comparisonEndWeekId,
    weekIdsInRange,
    comparisonWeekIdsInRange,
    comparisonWeekIdsForMainPeriod,
    isLastWeekWithPriorYear,
    isYTDWithPriorYear,
    baseParams
  };
};
